/**
 * 多裝置偏好同步的「欄位級合併」純函式（無 React / DOM 依賴，方便 deterministic 測試）。
 *
 * 後端是 last-write-wins 的整包 blob store，無法分辨「這次寫入只改了哪個 key」。
 * 為了避免兩台裝置同時編輯不同偏好時互相蓋掉（lost update），前端在偵測到雲端版本
 * 衝突（HTTP 409）時，用「本機目前快照 vs 上次成功推送的快照」做 per-key diff：
 *   - 本機自上次同步後真正改過的 key → 保留本機值（覆蓋雲端）。
 *   - 其餘 key → 採用雲端值（保留別台裝置的修改）。
 * 這是欄位級的 last-write-wins，不是 CRDT，也不需要在後端解析 payload。
 */

export type PrefsValues = Record<string, unknown>;

function stable(value: unknown): string {
  return value === undefined ? '\u0000undef' : JSON.stringify(value);
}

/**
 * 找出 `current` 相對 `base` 有變動的 key（新增 / 修改 / 刪除都算）。
 * 只比較 `keys` 清單內的欄位。
 */
export function computeChangedKeys(
  current: PrefsValues,
  base: PrefsValues,
  keys: readonly string[],
): string[] {
  const changed: string[] = [];
  for (const k of keys) {
    const hasCur = k in current;
    const hasBase = k in base;
    if (hasCur !== hasBase) { changed.push(k); continue; }
    if (hasCur && stable(current[k]) !== stable(base[k])) changed.push(k);
  }
  return changed;
}

/**
 * 欄位級合併：以雲端值為底，本機自上次同步後改過的 key 覆蓋上去。
 * `localBase` 是本機上次成功推送（或載入）的快照，用來判斷哪些 key 是本機改的。
 * 本機改成「刪除」的 key（base 有、current 無）→ 合併結果也移除（刪除傳播）。
 */
export function mergePayloads(
  cloud: PrefsValues,
  localCurrent: PrefsValues,
  localBase: PrefsValues,
  keys: readonly string[],
): PrefsValues {
  const changed = new Set(computeChangedKeys(localCurrent, localBase, keys));
  const merged: PrefsValues = {};
  for (const k of keys) {
    if (changed.has(k)) {
      // 本機在此 key 有編輯 → 本機優先；若本機刪除則略過（不放回）。
      if (k in localCurrent) merged[k] = localCurrent[k];
    } else if (k in cloud) {
      merged[k] = cloud[k];
    } else if (k in localCurrent) {
      merged[k] = localCurrent[k];
    }
  }
  return merged;
}

/** 兩份 payload 在 `keys` 範圍內是否有任何欄位不同。 */
export function payloadsDiffer(
  a: PrefsValues,
  b: PrefsValues,
  keys: readonly string[],
): boolean {
  return computeChangedKeys(a, b, keys).length > 0;
}
